/**
 * TemplatePicker: seletor ÚNICO de template da Meta (2026-09-14).
 *
 * Lista todos os templates aprovados com o que cada um tem (variáveis, mídia no
 * cabeçalho, botões). O que a tela não consegue usar aparece DESABILITADO com o
 * motivo, nunca some (regra da skill de paridade). Uma escolha salva que ficou
 * inválida continua visível, com o aviso, e nunca é limpa sozinha.
 *
 * A lista abre dentro do fluxo da tela (não flutua): funciona igual em drawer,
 * modal e no celular, sem ser cortada por área com rolagem.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Search, AlertTriangle } from 'lucide-react';
import {
  analyzeTemplate, templateUsability, templateBadges, uniqueApproved, findTemplate,
  CAPS_FULL,
} from '../../utils/templateParams';

const Badge = ({ children, colors }) => (
  <span style={{
    fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 999,
    border: `1px solid ${colors.borderColor}`, color: colors.textSecondary,
    whiteSpace: 'nowrap',
  }}>
    {children}
  </span>
);

const TemplatePicker = ({
  templates = [],
  value = '',
  language = '',
  onChange,
  capabilities = CAPS_FULL,
  colors,
  loading = false,
  disabled = false,
  placeholder = 'Selecione um template',
  noneLabel = null,
  emptyHint = 'Nenhum template aprovado. Crie em Templates da Meta.',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);

  const items = useMemo(() => uniqueApproved(templates).map((t) => {
    const analysis = analyzeTemplate(t);
    return { template: t, analysis, usability: templateUsability(analysis, capabilities) };
  }), [templates, capabilities]);

  const selectedTemplate = findTemplate(uniqueApproved(templates), value, language);
  const selected = selectedTemplate
    ? items.find((i) => i.template === selectedTemplate)
    : null;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter(({ template, analysis }) => (
      template.name.toLowerCase().includes(q) || analysis.body.text.toLowerCase().includes(q)
    ))
    : items;

  const pick = (template) => {
    setOpen(false);
    setQuery('');
    onChange?.(template);
  };

  const fieldStyle = {
    width: '100%', minHeight: 40, padding: '8px 10px', borderRadius: 8,
    border: `1px solid ${open ? colors.accentPrimary : colors.borderColor}`,
    backgroundColor: colors.bgPrimary, color: colors.textPrimary,
    display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.7 : 1, boxSizing: 'border-box', fontSize: 13,
  };

  const aviso = (texto) => (
    <div style={{
      display: 'flex', gap: 6, alignItems: 'flex-start', marginTop: 6,
      fontSize: 11.5, color: '#b45309', lineHeight: 1.4,
    }}>
      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{texto}</span>
    </div>
  );

  return (
    <div ref={rootRef} style={{ width: '100%' }}>
      <button
        type="button"
        onClick={() => !disabled && !loading && setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        style={fieldStyle}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {loading ? (
            <span style={{ color: colors.textTertiary }}>Carregando templates...</span>
          ) : value ? (
            <>
              <span style={{ fontWeight: 600, wordBreak: 'break-all' }}>{value}</span>
              {selected && templateBadges(selected.analysis).map((b) => (
                <Badge key={b} colors={colors}>{b}</Badge>
              ))}
            </>
          ) : (
            <span style={{ color: colors.textTertiary }}>{noneLabel || placeholder}</span>
          )}
        </span>
        {open ? <ChevronUp size={16} color={colors.textSecondary} /> : <ChevronDown size={16} color={colors.textSecondary} />}
      </button>

      {!open && value && !loading && templates.length > 0 && !selected
        && aviso('Este template não está mais entre os aprovados da conta: escolha outro.')}
      {!open && selected && !selected.usability.usable && aviso(selected.usability.reason)}

      {open && (
        <div style={{
          marginTop: 6, border: `1px solid ${colors.borderColor}`, borderRadius: 10,
          backgroundColor: colors.bgPrimary, overflow: 'hidden',
        }}>
          {items.length > 6 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
              borderBottom: `1px solid ${colors.borderColor}`,
            }}>
              <Search size={14} color={colors.textTertiary} />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar template"
                style={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  color: colors.textPrimary, fontSize: 13,
                }}
              />
            </div>
          )}
          <div role="listbox" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {noneLabel && (
              <button
                type="button"
                onClick={() => pick(null)}
                style={{
                  width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none',
                  borderBottom: `1px solid ${colors.borderColor}`,
                  backgroundColor: !value ? `${colors.accentPrimary}12` : 'transparent',
                  color: colors.textSecondary, fontSize: 13, cursor: 'pointer',
                }}
              >
                {noneLabel}
              </button>
            )}
            {!filtered.length && (
              <div style={{ padding: 14, fontSize: 12, color: colors.textTertiary }}>
                {items.length ? 'Nenhum template encontrado.' : emptyHint}
              </div>
            )}
            {filtered.map(({ template, analysis, usability }) => {
              const ativo = selectedTemplate === template;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={ativo}
                  aria-disabled={!usability.usable}
                  key={`${template.name}|${template.language}`}
                  onClick={() => usability.usable && pick(template)}
                  title={usability.reason || ''}
                  style={{
                    width: '100%', textAlign: 'left', padding: '10px 12px', border: 'none',
                    borderBottom: `1px solid ${colors.borderColor}`,
                    backgroundColor: ativo ? `${colors.accentPrimary}12` : 'transparent',
                    cursor: usability.usable ? 'pointer' : 'not-allowed',
                    display: 'flex', flexDirection: 'column', gap: 4,
                  }}
                >
                  <span style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 13, fontWeight: 600, wordBreak: 'break-all',
                      color: usability.usable ? colors.textPrimary : colors.textTertiary,
                    }}>
                      {template.name}
                    </span>
                    {template.language && <Badge colors={colors}>{template.language}</Badge>}
                    {templateBadges(analysis).map((b) => <Badge key={b} colors={colors}>{b}</Badge>)}
                  </span>
                  {analysis.body.text && (
                    <span style={{
                      fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.35,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                      overflow: 'hidden', opacity: usability.usable ? 1 : 0.6,
                    }}>
                      {analysis.body.text}
                    </span>
                  )}
                  {!usability.usable && (
                    <span style={{ fontSize: 11.5, color: colors.textTertiary, lineHeight: 1.35 }}>
                      {usability.reason}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatePicker;
